import { CircularProgress, useTheme } from "@mui/joy";
import { Box, CssBaseline, Grid, useMediaQuery } from "@mui/material";
import {
  useGetCurrentUserQuery,
  useSignOutMutation,
} from "@nerdjs/account-kit";
import { initNetworkAction } from "@nerdjs/nerd-network";
import { NerdNotifier, clearNotification } from "@nerdjs/nerd-ui";
import React, { ReactElement, useEffect, useMemo } from "react";
import { AppRouter } from "../../appRouter";
import { AppConfig } from "../../environement";
import { RouterConfig } from "../config/routerConfig";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useSelector } from "react-redux";
import {
  apiEndpointSelector,
  selectedBookletTokenSelector,
  setSelectedBookletToken,
} from "../../redux/rhapsodyReducer";
import { SignBooklet } from "../signBooklet/signBooklet";
import { useGetPublicBookletQuery } from "../../redux/booklet";
import { useSearchParams } from "react-router-dom";
import { useMissionMap } from "../../templates/projectInvite";
import { useDispatch } from "react-redux";
import { OnboardingRequired } from "../../routes/onboardingRequired";
import { Booklet } from "../../entities/booklet";

export default function NerdApp() {
  const dispatch = useDispatch();
  const apiEndpoint = useSelector(apiEndpointSelector);
  React.useEffect(() => {
    dispatch(initNetworkAction(AppConfig.api));
  }, []);

  if (apiEndpoint) return <App />;

  return <Box />;
}

/**
 * App wrapper
 *
 * @returns {ReactElement} App wrapper
 */
export function App(): ReactElement {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.down("md"), {
    noSsr: true,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const mission = useMissionMap(token!);

  const bookletToken = useSelector(selectedBookletTokenSelector);
  const { isSuccess } = useSignOutMutation()[1];
  const { contract } = useContract();
  const selectedBookletToken = useSelector(selectedBookletTokenSelector);

  useEffect(() => {
    dispatch(initNetworkAction(AppConfig.api));
  }, []);

  useEffect(() => {
    if (
      !selectedBookletToken &&
      !contract?.signed() &&
      !mission?.onboardingRequired()
    ) {
      dispatch(setSelectedBookletToken(mission?.BookletToken));
    }
  }, [contract]);

  const { isFetching } = useGetCurrentUserQuery();

  useEffect(() => {
    //If signout success, refresh window to reset redux state
    if (isSuccess) {
      window.location.replace(RouterConfig.root);
    }
  }, [isSuccess]);

  return (
    <NotifierWrapper>
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: md ? "column" : "row",
        }}
      >
        <CssBaseline />
        {isFetching ? (
          <Grid justifyContent={"center"} alignItems={"center"} container>
            <Grid item>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : (
          <>
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                display: "flex",
              }}
            >
              <OnboardingRequired />
              {bookletToken ? <SignBooklet /> : []}
              <AppRouter />
            </Box>
          </>
        )}
      </Box>
    </NotifierWrapper>
  );
}

const NotifierWrapper = ({ children }: { children: ReactElement }) => {
  const dispatch = useAppDispatch();
  const notification = useAppSelector((s) => s.notification);
  return (
    notification && (
      <NerdNotifier
        {...notification}
        onClose={() => {
          dispatch(clearNotification());
          notification.onClose?.();
        }}
      >
        {children}
      </NerdNotifier>
    )
  );
};

export function useContract() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const mission = useMissionMap(token!);
  const { data, isLoading } = useGetPublicBookletQuery(
    mission?.BookletToken ?? "",
    {
      skip: !mission?.BookletToken,
    },
  );

  return useMemo(
    () => ({
      contract: data ? new Booklet(data.booklet) : undefined,
      isLoading,
    }),
    [data, isLoading],
  );
}
